import { httpClient } from './httpClient'
import { apiUrl } from './apiBase'
import type { CombineScansRequest, ProcessScanRequest, ScanDto } from '@/models/library'

export const scansApi = {
  async listByFolder(folderId: string): Promise<ScanDto[]> {
    const { data } = await httpClient.get<ScanDto[]>(`/api/folders/${folderId}/scans`)
    return data
  },

  async upload(folderId: string, files: File[]): Promise<ScanDto[]> {
    const form = new FormData()
    for (const file of files) {
      form.append('files', file, file.name)
    }
    const { data } = await httpClient.post<ScanDto[]>(`/api/folders/${folderId}/scans`, form)
    return data
  },

  async rename(id: string, name: string): Promise<ScanDto> {
    const { data } = await httpClient.put<ScanDto>(`/api/scans/${id}`, { name })
    return data
  },

  async move(id: string, folderId: string): Promise<ScanDto> {
    const { data } = await httpClient.post<ScanDto>(`/api/scans/${id}/move`, { folderId })
    return data
  },

  async remove(id: string): Promise<void> {
    await httpClient.delete(`/api/scans/${id}`)
  },

  async combine(folderId: string, request: CombineScansRequest): Promise<ScanDto> {
    const { data } = await httpClient.post<ScanDto>(`/api/folders/${folderId}/combine`, request)
    return data
  },

  async process(id: string, request: ProcessScanRequest): Promise<ScanDto> {
    const { data } = await httpClient.post<ScanDto>(`/api/scans/${id}/process`, request)
    return data
  },

  async rotate(id: string, degrees: number): Promise<ScanDto> {
    const { data } = await httpClient.post<ScanDto>(`/api/scans/${id}/rotate`, { degrees })
    return data
  },

  async reorder(folderId: string, scanIds: string[]): Promise<void> {
    await httpClient.post(`/api/folders/${folderId}/scans/reorder`, { scanIds })
  },

  async ocr(id: string): Promise<ScanDto> {
    const { data } = await httpClient.post<ScanDto>(`/api/scans/${id}/ocr`, {})
    return data
  },

  async ocrText(id: string): Promise<string> {
    const { data } = await httpClient.get<{ text: string }>(`/api/scans/${id}/ocr-text`)
    return data.text
  },

  async get(id: string): Promise<ScanDto> {
    const { data } = await httpClient.get<ScanDto>(`/api/scans/${id}`)
    return data
  },

  /** Direct URL for the scan thumbnail (JPEG). Pass a version to bust the cache. */
  thumbnailUrl(id: string, version?: string): string {
    return apiUrl(`/api/scans/${id}/thumbnail${version ? `?v=${encodeURIComponent(version)}` : ''}`)
  },

  /** Direct URL for the scan content (processed if available). */
  contentUrl(id: string, opts: { download?: boolean; original?: boolean; version?: string } = {}): string {
    const params = new URLSearchParams()
    if (opts.download) params.set('download', 'true')
    if (opts.original) params.set('variant', 'original')
    if (opts.version) params.set('v', opts.version)
    const qs = params.toString()
    return apiUrl(`/api/scans/${id}/content${qs ? `?${qs}` : ''}`)
  }
}
